import { useParams } from "react-router-dom";
import { MACHINE_DOCUMENTS } from "../graphql/querys/items copy";
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useLazyQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";

export default function PublicMachinesForDocuments() {
  const { t } = useTranslation();
  const [openDocDialog, setOpenDocDialog] = useState(false);
  const [currentDoc, setCurrentDoc] = useState("");
  const [currentDocName, setCurrentDocName] = useState("");
  const [pin, setPin] = useState("");
  const { id } = useParams();
  const [PinDialogOpen, setPinDialogOpen] = useState(true);
  const [fetchDocuments, { loading, error, data }] = useLazyQuery(
    MACHINE_DOCUMENTS,
    {
      variables: { id, pin },
    }
  );

  const toDocumentUrl = (data) => {
    if (!data) return "";

    // New migrated format: URL/path in DB
    if (
      data.startsWith('/uploads/') ||
      data.startsWith('http://') ||
      data.startsWith('https://') ||
      data.startsWith('blob:')
    ) {
      return data;
    }

    // Legacy format: base64 in DB
    const base64WithoutPrefix = data.startsWith("data:")
      ? data.split(",")[1]
      : data;

    const bytes = atob(base64WithoutPrefix);
    let length = bytes.length;
    let out = new Uint8Array(length);

    while (length--) {
      out[length] = bytes.charCodeAt(length);
    }

    return URL.createObjectURL(new Blob([out], { type: "application/pdf" }));
  };

  const getDocumentName = (doc, idx) => {
    if (doc?.name) return doc.name;
    if (!doc?.data) return t("MACHINE_DETAIL.DOCUMENT_NUMBER", { number: idx + 1 });

    if (doc.data.startsWith("/uploads/")) {
      const lastPart = doc.data.split("/").pop() || "";
      return decodeURIComponent(lastPart) || t("MACHINE_DETAIL.DOCUMENT_NUMBER", { number: idx + 1 });
    }

    return t("MACHINE_DETAIL.DOCUMENT_NUMBER", { number: idx + 1 });
  };

  if (!id) {
    return (
      <Box minHeight="100vh" display="flex" alignItems="center" justifyContent="center" p={3}>
        <Typography variant="h6" textAlign="center">
          {t("PUBLIC_DOCS.INVALID_QR")}
        </Typography>
      </Box>
    );
  }

  const handleClose = () => {
    setOpenDocDialog(false);
    setCurrentDocName("");
    if (currentDoc) {
      URL.revokeObjectURL(currentDoc);
      setCurrentDoc("");
    }
  };

  return (
    <>
      {data ? (
        <Grid
          container
          direction="column"
          spacing={2}
          sx={{ maxWidth: 900, mx: "auto", p: { xs: 2, sm: 3 } }}
        >
          {data.machineDocuments.map((doc, idx) => {
            const url = toDocumentUrl(doc.data);
            const documentName = getDocumentName(doc, idx);

            return (
              <Grid item key={idx}>
                <Card variant="outlined">
                  <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={2}
                      alignItems={{ xs: "stretch", sm: "center" }}
                      justifyContent="space-between"
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          variant="h6"
                          sx={{
                            fontSize: { xs: "1rem", sm: "1.1rem" },
                            wordBreak: "break-word",
                          }}
                        >
                          {documentName}
                        </Typography>
                      </Box>
                      <Button
                        variant="contained"
                        sx={{ width: { xs: "100%", sm: "auto" } }}
                        onClick={() => {
                          setCurrentDoc(url);
                          setCurrentDocName(documentName);
                          setOpenDocDialog(true);
                        }}
                      >
                        {t("PUBLIC_DOCS.VIEW_DOCUMENT")}
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      ) : (
        <>
          <Dialog
            open={PinDialogOpen}
            onClose={() => setPinDialogOpen(false)}
            fullWidth
            maxWidth="xs"
          >
            <Box
              p={2}
              component="form"
              onSubmit={(e) => {
                e.preventDefault();
                fetchDocuments();
              }}
            >
              <Typography variant="h6">
                {t("PUBLIC_DOCS.ENTER_PIN")}
              </Typography>
              <TextField
                type="password"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                label="PIN"
                fullWidth
                margin="normal"
              />
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                disabled={loading}
              >
                {loading ? t("COMMON.LOADING") : t("ACTIONS.SUBMIT")}
              </Button>
            </Box>
          </Dialog>
        </>
      )}
      {!data && error ? (
        <Box sx={{ px: 2, pb: 2, textAlign: "center" }}>
          <Typography color="error">
            {t("PUBLIC_DOCS.LOAD_ERROR")}
          </Typography>
        </Box>
      ) : null}
      <Dialog
        open={openDocDialog}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            width: { xs: "calc(100% - 16px)", sm: "calc(100% - 48px)" },
            m: { xs: 1, sm: 3 },
          },
        }}
      >
        <DialogTitle sx={{ pb: 1, wordBreak: "break-word" }}>
          {currentDocName || t("MACHINE_DETAIL.DOCUMENT_NUMBER", { number: "" }).trim()}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Box
            sx={{
              width: "100%",
              height: { xs: "55vh", sm: "65vh" },
              borderRadius: 1,
              overflow: "hidden",
              bgcolor: "#f5f5f5",
            }}
          >
            <iframe
              title="doc"
              src={currentDoc ? `${currentDoc}#toolbar=0` : ""}
              width="100%"
              height="100%"
              style={{ border: "none" }}
              allowFullScreen
            >
              <a href={currentDoc} download="document.pdf">
                {t("PUBLIC_DOCS.DOWNLOAD_IF_NOT_VISIBLE")}
              </a>
            </iframe>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            component="a"
            href={currentDoc}
            target="_blank"
            rel="noreferrer"
          >
            {t("PUBLIC_DOCS.OPEN_NEW_TAB")}
          </Button>
          <Button onClick={handleClose} color="primary" variant="contained">
            {t("ACTIONS.CLOSE")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
